import React from "react";
import { useI18n } from "@/contexts/I18nContext";
import { Cloud, Sun, Wind, Droplets, Thermometer } from "lucide-react";

function fmt(n, unit = "") { return (n == null ? "–" : `${Math.round(n * 10) / 10}${unit}`); }

// Map WMO weather codes to translation keys (we collapse some variants to the base key)
const codeKey = {
  0: "wcond_0", 1: "wcond_1", 2: "wcond_2", 3: "wcond_3",
  45: "wcond_45", 48: "wcond_45",
  51: "wcond_51", 53: "wcond_53", 55: "wcond_55",
  61: "wcond_61", 63: "wcond_63", 65: "wcond_65",
  71: "wcond_71", 73: "wcond_71", 75: "wcond_71",
  80: "wcond_80", 81: "wcond_80", 82: "wcond_82",
  95: "wcond_95", 96: "wcond_95", 99: "wcond_95",
};

export default function WeatherCard({ data }) {
  const { t } = useI18n();
  const current = data?.weather?.current;
  const daily = data?.weather?.daily;
  if (!current) {
    return (
      <div data-testid="weather-card" className="clay p-6 md:p-7 relative overflow-hidden">
        <p className="eyebrow">{t("weather")} · {t("today")}</p>
        <p className="text-sm text-[color:var(--ink-2)] italic mt-3">
          {t("weather_unavailable")}
        </p>
      </div>
    );
  }
  const wcode = current.weather_code;
  const conditionKey = codeKey[wcode];
  const condition = conditionKey ? t(conditionKey) : "—";

  return (
    <div data-testid="weather-card" className="clay p-6 md:p-7 relative overflow-hidden">
      <div className="flex items-start justify-between">
        <div>
          <p className="eyebrow">{t("weather")} · {t("today")}</p>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-5xl font-bold" style={{ fontFamily: "Outfit" }} data-testid="weather-temp">
              {fmt(current.temperature_2m, "°")}
            </span>
            <span className="text-sm text-[color:var(--ink-2)]">C</span>
          </div>
          <p className="text-[color:var(--ink-2)] mt-1">{condition}</p>
        </div>
        <div className="w-14 h-14 rounded-2xl bg-[color:var(--coral)]/10 text-[color:var(--coral)] flex items-center justify-center">
          {wcode === 0 || wcode === 1 ? <Sun className="w-7 h-7" /> : <Cloud className="w-7 h-7" />}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-3">
        <Tile icon={<Wind className="w-4 h-4" />} label={t("wind")} value={fmt(current.wind_speed_10m, " km/h")} testid="weather-wind" />
        <Tile icon={<Droplets className="w-4 h-4" />} label="RH" value={fmt(current.relative_humidity_2m, "%")} testid="weather-humidity" />
        <Tile icon={<Thermometer className="w-4 h-4" />} label={`${t("high")} / ${t("low")}`} value={`${fmt(daily?.temperature_2m_max?.[0], "°")} / ${fmt(daily?.temperature_2m_min?.[0], "°")}`} testid="weather-hilo" />
      </div>

      <div className="mt-5 flex items-center justify-between text-xs text-[color:var(--ink-2)]">
        <span>{t("sunrise")}: <span className="mono">{(daily?.sunrise?.[0] || "—").slice(11, 16)}</span></span>
        <span>{t("sunset")}: <span className="mono">{(daily?.sunset?.[0] || "—").slice(11, 16)}</span></span>
        <span>UV max: <span className="mono">{fmt(daily?.uv_index_max?.[0])}</span></span>
      </div>
    </div>
  );
}

function Tile({ icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl bg-[color:var(--sand-2)] p-3">
      <div className="flex items-center gap-1.5 text-[color:var(--ink-2)] text-[11px] uppercase tracking-wider">
        {icon}<span>{label}</span>
      </div>
      <div className="font-semibold mt-1 text-[color:var(--ink)]" style={{ fontFamily: "Outfit" }}>{value}</div>
    </div>
  );
}
